export const systemPage = {
  eyebrow: 'How It Works',
  title: 'Rules become constraints. Execution becomes visible. Patterns surface across runs.',
  lede: 'AI in a chat window leaves a trail you can’t audit. Proof treats the work as a controlled system instead.',
  actions: [
    { label: 'See a real run →', to: '/demo' },
    { label: 'Open the console →', to: '/console', primary: true },
  ],
  overviewImage: {
    src: '/demo/architecture-overview.svg',
    alt: 'Overview diagram showing how requests flow through standards, planning, approval, execution, and evidence capture.',
  },
  sections: [
    {
      pillar: 'Standards',
      question: 'How do you make AI actually follow the rules?',
      tag: 'Rules become tools the AI must use, not prose it can ignore.',
      philosophy:
        'You’ve written your coding standards. You’ve documented every convention. The AI reads them and still takes shortcuts. The problem isn’t that the rules don’t exist. They’re suggestions, not constraints.',
      body: [
        "Proof turns your rules into a tool the AI calls before writing code. The AI calls <strong>search_standards('error handling')</strong> and gets back structured requirements. Not prose to skim, but a checklist it must satisfy.",
        'That checklist stays attached to the step through implementation and verification.',
      ],
      list: [
        '<strong>Search</strong> — the AI looks up your project’s standards for patterns relevant to the current task',
        '<strong>Extract</strong> — structured requirements come back: MUST use AppError subclasses, NEVER catch-log-continue',
        '<strong>Implement</strong> — the AI writes code against that checklist',
        '<strong>Verify</strong> — compliance is checked before the step declares done',
      ],
      punchline: 'The difference is not whether the model read your rules. The difference is whether it is allowed to ignore them.',
      image: {
        src: '/demo/harness-engineering.svg',
        alt: 'Diagram showing a step shaped by knowledge retrieval, standards requirements, workspace rules, and delivery controls.',
      },
      references: [
        {
          title: 'My AI Adoption Journey',
          href: 'https://mitchellh.com/writing/my-ai-adoption-journey',
          note: 'Step 5: engineer the harness around the model.',
        },
      ],
    },
    {
      pillar: 'Control',
      question: 'What happens between your approval and the result?',
      tag: 'You approve the plan. The system turns it into controlled execution.',
      philosophy:
        'A planning mode can sketch an outline. Repository work needs more than an outline. Proof puts a gate between the plan and the code.',
      body: [
        'After you approve the plan, the system breaks your request into typed steps — research, code, test, review — each with explicit scope and declared dependencies.',
        'That is the difference from ordinary planning mode: the plan is not just text in a chat window. It becomes the execution contract for the run.',
      ],
      list: [
        '<strong>Dependency ordering</strong> — later steps build on earlier steps’ verified output, not on fading context',
        '<strong>Isolated execution</strong> — each step gets its own workspace and tool configuration, so failure stays local',
        '<strong>Controlled delivery</strong> — steps cannot push, delete branches, or access credentials directly',
        '<strong>Failure containment</strong> — if step 5 of 7 fails, steps 1–4 are complete and preserved',
      ],
      punchline: 'You approve before risk, not after failure. Your repository stays coherent even when a step goes wrong.',
      image: {
        src: '/demo/control-flow.svg',
        alt: 'Control flow diagram showing an approved plan entering controlled execution with isolated workspaces and failure containment.',
      },
    },
    {
      pillar: 'Evidence',
      question: 'How do you know what the AI actually did, across one run and across many?',
      tag: 'You can inspect any single run. You can also see patterns across recent runs.',
      philosophy:
        'Chat sessions end. The reasoning disappears. Three months later, you need to understand why a choice was made and the context is gone. Proof preserves every run and analyzes patterns across them.',
      body: [
        'Inside a single run: every step’s tool calls, standards lookups, diffs, tests, and outputs are captured. You can inspect any decision the AI surfaced.',
        'Across recent runs: the system aggregates outcomes, evidence coverage, and recurring guidance gaps. Patterns that would otherwise live only in your head become visible.',
        'What other tools don’t have: tenant-scoped run history, plus the Learning Signals analytics that surface patterns across it.',
      ],
      list: [
        '<strong>Run record</strong> — plan, steps, conversations, tool calls, outputs, tests, failures, diagnostics, corrections',
        '<strong>Standards evidence</strong> — which standards were consulted and which requirements were verified',
        '<strong>Diagnostics</strong> — token usage, duration, cost, model selection per step',
        '<strong>Cross-run analytics</strong> — outcome distribution, evidence coverage, and recurring gap topics across your recent runs',
        '<strong>Tenant scope</strong> — your records, your patterns, your insights. No cross-tenant aggregation',
      ],
      punchline: 'Other tools show you the last diff. Proof keeps each run inspectable and analyzes patterns across many.',
      image: {
        src: '/demo/experience-capture.svg',
        alt: 'Diagram showing run artifacts being captured, explained, and made available for review.',
      },
      references: [
        {
          title: 'Exactly Why and How AI Will Replace Knowledge Work',
          href: 'https://danielmiessler.com/blog/exactly-why-and-how-ai-will-replace-knowledge-work',
          note: 'The articulation gap and expertise ratchet.',
        },
      ],
    },
    {
      pillar: 'Compound',
      question: 'Do corrections actually stick?',
      tag: 'Every correction becomes a constraint the AI can’t ignore next time.',
      philosophy:
        'Today: you correct the AI, you write the lesson as a rule, you hope the next session reads it. With Proof, the writing is still manual. But enforcement is not.',
      body: [
        'You identify the gap during review. You write the correction as a standard. Proof provides the format. From that point, every run that touches that pattern finds the standard, extracts the requirements, and verifies compliance.',
        'The Learning Signals panel shows whether corrections are working. If the same gap reappears across runs, it’s visible. If recurrence drops, that’s visible too.',
      ],
      list: [
        '<strong>Run 1</strong> — review finds the AI used catch-log-continue. You correct it and add an error handling standard.',
        '<strong>Run 2</strong> — the AI searches standards, finds the new rule, follows it. Review finds another gap.',
        '<strong>Run 3</strong> — both standards enforced. Clean first pass.',
      ],
      punchline: 'Not because the model got smarter. Because the system kept the lesson and enforced it.',
      image: {
        src: '/demo/compound-engineering.svg',
        alt: 'Diagram showing three runs where corrections become standards and later runs start stronger.',
      },
      references: [
        {
          title: 'Compound Engineering: How Every Codes with Agents',
          href: 'https://every.to/chain-of-thought/compound-engineering-how-every-codes-with-agents',
          note: 'The plan, work, review, compound loop.',
        },
      ],
    },
    {
      pillar: 'Planning',
      question: 'How does the system build the plan?',
      tag: 'Context discovered when needed, not dumped upfront.',
      philosophy:
        'You’ve seen it: the AI nails the first step, then by the fourth it’s forgotten the conventions it followed earlier. A single long session fills up and early decisions drop out. Proof’s planner avoids this by building the plan in stages, pulling only what each stage needs.',
      body: [
        'The planner works through five stages. Each produces an artifact you can inspect. If stage 3 fails, stages 1–2 are preserved.',
        'If you already have a plan from Claude Code, Cursor, or your own review, that plan can be the input. Proof still shapes it into typed steps with standards, scope, and verification before execution starts.',
      ],
      list: [
        '<strong>Understand</strong> — extract what you asked for, what constraints apply, and how to tell when it’s done',
        '<strong>Generate</strong> — produce the step list with types, dependencies, and descriptions',
        '<strong>Shape</strong> — attach relevant standards to each step',
        '<strong>Ground</strong> — resolve file paths, verify code references, anchor the plan to your actual codebase',
        '<strong>Validate</strong> — check that the plan covers your request and every step has standards attached',
      ],
      punchline: 'Planning is useful only if execution stays faithful to it. The planner’s job is to produce a plan the system can actually enforce.',
      image: {
        src: '/demo/progressive-disclosure.svg',
        alt: 'Diagram comparing dumping all context upfront with staged planning that pulls context on demand.',
      },
      references: [
        {
          title: 'Context Engineering for Coding Agents',
          href: 'https://martinfowler.com/articles/exploring-gen-ai/context-engineering-coding-agents.html',
          note: 'Why retrieval shape and timing change execution quality.',
        },
      ],
    },
  ],
}
